import { Button, SettingsCard, Typography, useToast } from "@humansignal/ui";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { Form, Input, Toggle } from "../../components/Form";
import { useAPI } from "../../providers/ApiProvider";
import { ProjectContext } from "../../providers/ProjectProvider";
import { cn } from "../../utils/bem";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { useTranslation } from "../../i18n/LocaleProvider";

/**
 * Project-scoped security settings — manager-only.
 *
 * Two layers of decision-making:
 *   1. Profile dropdown picks one of the named templates, grouped
 *      by what they're optimised for:
 *        - Basic (default) — Balanced defaults for most projects.
 *        - Compliance — Strict, HIPAA-ready, GDPR-aware.
 *        - Operational — Crowdsource, Internal R&D, Permissive.
 *        - Custom — hand-tune each field.
 *   2. Custom unlocks the individual toggles + integer inputs so
 *      the manager can hand-tune each field.
 *
 * Each field is backed by a real model column. Enforcement status
 * tracks per-field in docs/security.md (native-apps branch). Some
 * fields are enforced today (offline-allow toggles, lock /
 * delete / anonymize, no-store middleware, wipe-on-logout,
 * restrict-export-to-managers); the rest store correctly but
 * await dedicated enforcement layers (audit-log model, idle-timer,
 * cache-TTL, re-auth flow).
 */

const SECURITY_FIELDS = [
  // booleans
  "allow_offline_for_annotators",
  "allow_offline_for_reviewers",
  "allow_multi_device",
  "allow_multi_user_per_device",
  "require_offline_unlock",
  "lock_annotations_after_submit",
  "annotators_can_delete_annotations",
  "anonymize_annotators_in_exports",
  "prevent_browser_caching",
  "wipe_cache_on_logout",
  "restrict_export_to_managers",
  "audit_log_annotator_actions",
  "require_fresh_session_for_export",
  // integers
  "session_idle_timeout_minutes",
  "max_offline_cache_days",
];

const INTEGER_FIELDS = new Set(["session_idle_timeout_minutes", "max_offline_cache_days"]);

// Each template is a partial — fields not listed inherit from
// `Balanced`, the implicit baseline.
const BALANCED = {
  allow_offline_for_annotators: true,
  allow_offline_for_reviewers: true,
  allow_multi_device: true,
  allow_multi_user_per_device: true,
  require_offline_unlock: true,
  lock_annotations_after_submit: false,
  annotators_can_delete_annotations: true,
  anonymize_annotators_in_exports: false,
  prevent_browser_caching: false,
  wipe_cache_on_logout: true,
  restrict_export_to_managers: false,
  audit_log_annotator_actions: false,
  require_fresh_session_for_export: false,
  session_idle_timeout_minutes: 0,
  max_offline_cache_days: 0,
};

const TEMPLATES = {
  strict: {
    label: "Strict, sensitive data (PII / medical / legal)",
    summary:
      "No offline use, no edits after submit, no annotator deletes, anonymized exports, no browser cache, full logout wipe, exports manager-only, idle auto-logout at 15 min. Recommended for compliance-bound projects.",
    values: {
      ...BALANCED,
      allow_offline_for_annotators: false,
      allow_offline_for_reviewers: false,
      allow_multi_device: false,
      allow_multi_user_per_device: false,
      lock_annotations_after_submit: true,
      annotators_can_delete_annotations: false,
      anonymize_annotators_in_exports: true,
      prevent_browser_caching: true,
      wipe_cache_on_logout: true,
      restrict_export_to_managers: true,
      audit_log_annotator_actions: true,
      require_fresh_session_for_export: true,
      session_idle_timeout_minutes: 15,
      max_offline_cache_days: 0,
    },
  },
  hipaa: {
    label: "HIPAA-ready, clinical / medical records",
    summary:
      "Audit-logged everything, anonymized exports, exports manager-only, 15-min idle logout, fresh session for export, no browser cache. Offline labeling allowed for clinicians on managed devices.",
    values: {
      ...BALANCED,
      anonymize_annotators_in_exports: true,
      prevent_browser_caching: true,
      restrict_export_to_managers: true,
      audit_log_annotator_actions: true,
      require_fresh_session_for_export: true,
      session_idle_timeout_minutes: 15,
      max_offline_cache_days: 7,
    },
  },
  gdpr: {
    label: "GDPR-aware. EU personal data",
    summary:
      "Anonymized exports + audit log, browser cache off, 30-min idle logout, max offline retention 30 days, full logout wipe. Right-to-be-forgotten compatible (per-row deletes still flow through Django).",
    values: {
      ...BALANCED,
      anonymize_annotators_in_exports: true,
      prevent_browser_caching: true,
      audit_log_annotator_actions: true,
      session_idle_timeout_minutes: 30,
      max_offline_cache_days: 30,
    },
  },
  balanced: {
    label: "Basic, most projects",
    summary:
      "Defaults: offline labeling allowed, edits + deletes by the annotator are fine, exports keep real annotator names, browser cache enabled, logout wipes the local cache, no idle timeout.",
    values: BALANCED,
  },
  crowdsource: {
    label: "Crowdsource, many gig annotators",
    summary:
      "No offline (data stays cloud-side), annotations lock on submit, anonymized exports, exports manager-only, no annotator deletes, audit logged. Discourages corpus extraction.",
    values: {
      ...BALANCED,
      allow_offline_for_annotators: false,
      lock_annotations_after_submit: true,
      annotators_can_delete_annotations: false,
      anonymize_annotators_in_exports: true,
      restrict_export_to_managers: true,
      audit_log_annotator_actions: true,
      session_idle_timeout_minutes: 30,
    },
  },
  internal_rd: {
    label: "Internal R&D, trusted researchers",
    summary:
      "Permissive on edits + identity, but audit-logged so changes are traceable. Cache survives logout (researchers iterate fast), no idle timeout, no offline unlock prompt. Use for trusted internal teams.",
    values: {
      ...BALANCED,
      audit_log_annotator_actions: true,
      wipe_cache_on_logout: false,
      require_offline_unlock: false,
    },
  },
  permissive: {
    label: "Permissive, internal / public datasets",
    summary:
      "Everything open. Annotators can delete + edit freely, exports include real identities, browsers cache normally, the local cache survives logouts to skip re-downloading, no offline unlock prompt. Use only for non-sensitive content.",
    values: {
      ...BALANCED,
      wipe_cache_on_logout: false,
      require_offline_unlock: false,
    },
  },
};

function detectTemplate(project) {
  for (const [key, tpl] of Object.entries(TEMPLATES)) {
    const matches = SECURITY_FIELDS.every((f) => {
      const a = INTEGER_FIELDS.has(f) ? Number(project[f] ?? 0) : !!project[f];
      const b = INTEGER_FIELDS.has(f) ? Number(tpl.values[f] ?? 0) : !!tpl.values[f];
      return a === b;
    });
    if (matches) return key;
  }
  return "custom";
}

export const SecuritySettings = () => {
  const { t } = useTranslation();
  const { project, fetchProject } = useContext(ProjectContext);
  const api = useAPI();
  const toast = useToast();

  const initialTemplate = useMemo(() => (project?.id ? detectTemplate(project) : "balanced"), [project]);
  const [activeTemplate, setActiveTemplate] = useState(initialTemplate);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Data retention is a PLATFORM-owner control (the serializer drops the
  // field for non-staff), so we only render it for staff/superusers.
  const { user } = useAuth();
  const isStaff = !!(user?.is_staff || user?.is_superuser);
  const [retentionDays, setRetentionDays] = useState(project?.data_retention_days ?? 0);
  const [savingRetention, setSavingRetention] = useState(false);
  useEffect(() => {
    setRetentionDays(project?.data_retention_days ?? 0);
  }, [project?.id, project?.data_retention_days]);

  const saveRetention = useCallback(async () => {
    if (!project?.id) return;
    setSavingRetention(true);
    const res = await api.callApi("updateProject", {
      params: { pk: project.id },
      body: { data_retention_days: Number(retentionDays) || 0 },
    });
    setSavingRetention(false);
    if (res && !res.error) {
      fetchProject(project.id, true);
      toast.show({ message: "Retention updated" });
    } else {
      toast.show({ message: res?.error ?? "Failed to save", type: "error" });
    }
  }, [project, api, retentionDays, fetchProject, toast]);

  useUpdatePageTitle(createTitleFromSegments([project?.title, "Security"]));

  const onSaved = useCallback(() => {
    if (project?.id) fetchProject(project.id, true);
    toast.show({ message: t("pages.settings.security_settings.security_settings_saved") });
  }, [project, fetchProject, toast]);

  const handleFormChange = useCallback(() => {
    if (activeTemplate !== "custom") setActiveTemplate("custom");
  }, [activeTemplate]);

  const applyTemplate = useCallback(
    async (key) => {
      if (!project?.id) return;
      setActiveTemplate(key);
      if (key === "custom") return;
      const values = TEMPLATES[key].values;
      setApplyingTemplate(true);
      const res = await api.callApi("updateProject", {
        params: { pk: project.id },
        body: values,
      });
      setApplyingTemplate(false);
      if (!res || res.error) {
        toast.show({
          message: res?.error ?? "Failed to apply template",
          type: "error",
        });
        return;
      }
      fetchProject(project.id, true);
      toast.show({
        message: `Applied "${TEMPLATES[key].label.split(", ")[0]}" template`,
      });
    },
    [project, api, toast, fetchProject],
  );

  if (!project?.id) return null;

  const lockedByTemplate = activeTemplate !== "custom";

  return (
    <div className={cn("security-settings").toClassName()}>
      <div className={cn("settings-page").toClassName()}>
        <SettingsPageHeader
          title={t("pages.settings.security_settings.security")}
          subtitle={t("pages.settings.security_settings.lock_down_how_this_projects_data_leaves_")}
        />

        <SettingsCard
          title="Profile"
          description={t("pages.settings.security_settings.pick_a_security_profile_for_this_project")}
        >
          <select
            value={activeTemplate}
            onChange={(e) => applyTemplate(e.target.value)}
            disabled={applyingTemplate}
            style={{
              width: "100%",
              maxWidth: 520,
              height: 40,
              minHeight: 40,
              boxSizing: "border-box",
              padding: "0 16px",
              borderRadius: 5,
              border: "1px solid var(--color-neutral-border)",
              background: "var(--color-neutral-background)",
              color: "var(--color-neutral-content)",
              fontSize: "var(--font-size-field-input)",
              lineHeight: "var(--line-height-snug)",
            }}
          >
            {/* Basic stays at the top, ungrouped, so it reads as the
                obvious default for new projects. The "(default)" hint
                is part of the visible label so the dropdown communicates
                this even when collapsed. The other profiles are grouped
                by what they're optimised for: regulated / compliance
                work, operational use-cases, then Custom for hand-tuned
                setups. */}
            <option value="balanced">{t("pages.settings.security_settings.basic_most_projects_default")}</option>
            <optgroup label={t("pages.settings.security_settings.compliance")}>
              <option value="strict">
                {t("pages.settings.security_settings.strict_sensitive_data_pii_medical_legal")}
              </option>
              <option value="hipaa">{t("pages.settings.security_settings.hipaaready_clinical_medical_records")}</option>
              <option value="gdpr">{t("pages.settings.security_settings.gdpraware_eu_personal_data")}</option>
            </optgroup>
            <optgroup label={t("pages.settings.security_settings.operational")}>
              <option value="crowdsource">
                {t("pages.settings.security_settings.crowdsource_many_gig_annotators")}
              </option>
              <option value="internal_rd">
                {t("pages.settings.security_settings.internal_rd_trusted_researchers")}
              </option>
              <option value="permissive">
                {t("pages.settings.security_settings.permissive_internal_public_datasets")}
              </option>
            </optgroup>
            <optgroup label={t("pages.settings.security_settings.custom")}>
              <option value="custom">{t("pages.settings.security_settings.custom_design_your_own")}</option>
            </optgroup>
          </select>
          <Typography size="small" className="text-neutral-content-subtler mt-2">
            {activeTemplate === "custom"
              ? "All fields below are editable. Changes save when you press Save."
              : TEMPLATES[activeTemplate].summary}
          </Typography>
        </SettingsCard>

        {isStaff && (
          <SettingsCard
            title="Data retention"
            description="Delete this project's data (tasks, annotations, uploads, exports) after a set age; the empty project stays. 0 means the organisation's default. Owners are warned by email and in the app before anything is deleted. Platform staff only."
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 520 }}>
              <label htmlFor="retention_days" style={{ fontSize: "var(--font-size-body-small)", fontWeight: 500 }}>
                Delete this project's data after (days)
              </label>
              {/* Box and button share one height: 32, the settings pages' button. */}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  id="retention_days"
                  type="number"
                  min={0}
                  max={3650}
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(e.target.value)}
                  style={{
                    width: 160,
                    height: 32,
                    boxSizing: "border-box",
                    padding: "0 12px",
                    border: "1px solid var(--color-neutral-border)",
                    borderRadius: 5,
                    background: "var(--color-neutral-background)",
                    color: "var(--color-neutral-content)",
                    fontSize: "var(--font-size-body-small)",
                  }}
                />
                <Button
                  size="small"
                  style={{ width: 120 }}
                  onClick={saveRetention}
                  disabled={savingRetention}
                  waiting={savingRetention}
                >
                  Save
                </Button>
              </div>
            </div>
          </SettingsCard>
        )}

        <Form action="updateProject" formData={{ ...project }} params={{ pk: project.id }} onSubmit={onSaved}>
          <div
            className={cn("settings-page").elem("body").toClassName()}
            onChange={handleFormChange}
            style={{ opacity: lockedByTemplate ? 0.6 : 1 }}
          >
            <SettingsCard
              title={t("pages.settings.security_settings.offline_access")}
              description={t("pages.settings.security_settings.whether_the_desktop_mobile_apps_can_cach")}
            >
              <Form.Row columnCount={1}>
                <Toggle
                  name="allow_offline_for_annotators"
                  label={t("pages.settings.security_settings.allow_annotators_to_work_offline")}
                  description={t("pages.settings.security_settings.when_off_the_desktop_mobile_app_refuses_")}
                />
                <Toggle
                  name="allow_offline_for_reviewers"
                  label={t("pages.settings.security_settings.allow_reviewers_to_work_offline")}
                  description={t("pages.settings.security_settings.same_rule_for_reviewers")}
                />
                <Toggle
                  name="require_offline_unlock"
                  label={t("pages.settings.security_settings.require_password_to_open_offline")}
                  description={t("pages.settings.security_settings.when_on_default_opening_this_project_off")}
                />
                <Toggle
                  name="allow_multi_device"
                  label={t("pages.settings.security_settings.allow_the_same_annotator_on_multiple_dev")}
                  description={t("pages.settings.security_settings.when_off_each_annotator_can_only_have_th")}
                />
                <Toggle
                  name="allow_multi_user_per_device"
                  label={t("pages.settings.security_settings.allow_multiple_users_on_the_same_device")}
                  description={t("pages.settings.security_settings.when_off_only_one_annotator_can_have_thi")}
                />
                <Input
                  name="max_offline_cache_days"
                  label={t("pages.settings.security_settings.max_offline_cache_age_days")}
                  type="number"
                  min="0"
                  description={t("pages.settings.security_settings.when_0_locallycached_data_is_invalidated")}
                />
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.security_settings.annotation_modifications")}
              description={t("pages.settings.security_settings.what_annotators_can_do_to_a_row_once_the")}
            >
              <Form.Row columnCount={1}>
                <Toggle
                  name="lock_annotations_after_submit"
                  label={t("pages.settings.security_settings.lock_annotations_after_submit")}
                  description={t("pages.settings.security_settings.when_on_an_annotation_cannot_be_edited_o")}
                />
                <Toggle
                  name="annotators_can_delete_annotations"
                  label={t("pages.settings.security_settings.annotators_can_delete_their_own_annotati")}
                  description={t("pages.settings.security_settings.when_off_only_managers_and_reviewers_can")}
                />
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.security_settings.identity_audit")}
              description={t("pages.settings.security_settings.what_realworld_identity_info_reaches_dow")}
            >
              <Form.Row columnCount={1}>
                <Toggle
                  name="anonymize_annotators_in_exports"
                  label={t("pages.settings.security_settings.anonymize_annotators_in_exports")}
                  description={t("pages.settings.security_settings.replaces_user_id_email_in_exports_with_a")}
                />
                <Toggle
                  name="audit_log_annotator_actions"
                  label={t("pages.settings.security_settings.audit_log_annotator_actions")}
                  description={t("pages.settings.security_settings.records_every_annotator_view_edit_delete")}
                />
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.security_settings.export_controls")}
              description={t("pages.settings.security_settings.who_can_fetch_raw_exports_and_whether_re")}
            >
              <Form.Row columnCount={1}>
                <Toggle
                  name="restrict_export_to_managers"
                  label="Restrict export to managers"
                  description={t("pages.settings.security_settings.when_on_annotators_see_their_own_work_in")}
                />
                <Toggle
                  name="require_fresh_session_for_export"
                  label={t("pages.settings.security_settings.require_fresh_session_for_export")}
                  description={t("pages.settings.security_settings.prompts_the_user_to_reenter_their_passwo")}
                />
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.security_settings.browser_caching")}
              description={t("pages.settings.security_settings.whether_annotation_data_may_sit_in_a_use")}
            >
              <Form.Row columnCount={1}>
                <Toggle
                  name="prevent_browser_caching"
                  label={t("pages.settings.security_settings.prevent_browser_caching_of_this_projects")}
                  description={t("pages.settings.security_settings.when_on_every_api_page_response_is_sent_")}
                />
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.security_settings.session_logout")}
              description={t("pages.settings.security_settings.when_the_user_is_autologgedout_and_what_")}
            >
              <Form.Row columnCount={1}>
                <Input
                  name="session_idle_timeout_minutes"
                  label={t("pages.settings.security_settings.idle_timeout_minutes")}
                  type="number"
                  min="0"
                  description={t("pages.settings.security_settings.when_0_the_spa_autologs_out_after_this_m")}
                />
                <Toggle
                  name="wipe_cache_on_logout"
                  label={t("pages.settings.security_settings.wipe_cache_on_logout")}
                  description={t("pages.settings.security_settings.when_on_default_the_desktop_mobile_app_d")}
                />
              </Form.Row>
            </SettingsCard>
          </div>

          <Form.Actions>
            <Form.Indicator>
              <span case="success">Saved!</span>
            </Form.Indicator>
            <Button
              type="submit"
              className="w-[120px]"
              aria-label={t("pages.settings.security_settings.save_security_settings")}
            >
              Save
            </Button>
          </Form.Actions>
        </Form>
      </div>
    </div>
  );
};

SecuritySettings.menuItem = "Security";
SecuritySettings.title = "Security";
SecuritySettings.path = "/security";
SecuritySettings.exact = true;
